/**
 * Conversion d'une valeur inconnue en texte affichable.
 *
 * Le `dataProvider` lit du JSON : toute valeur d'enregistrement est un `unknown`.
 * `String(value)` fonctionne pour les nombres et les chaines, mais rend
 * `[object Object]` sur un objet imbrique — et les enregistrements de cette API en
 * contiennent (`conservationState`, `stockUsages`, `relatedBatchIds`). Afficher
 * `[object Object]` dans une colonne de tableau est un defaut silencieux : rien
 * n'echoue, l'utilisateur lit juste une case inutile.
 *
 * Cette fonction est donc le seul endroit ou une valeur inconnue devient du texte,
 * et elle traite chaque cas explicitement.
 */
export const asDisplayString = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toLocaleString('fr-FR') : '';
  }

  if (typeof value === 'boolean') {
    return value ? 'Oui' : 'Non';
  }

  if (Array.isArray(value)) {
    return value.map((entry) => asDisplayString(entry)).join(', ');
  }

  if (value instanceof Date) {
    return value.toLocaleString('fr-FR');
  }

  if (typeof value === 'object') {
    // Un objet imbrique reste lisible en JSON compact, la ou `String()` rendrait
    // `[object Object]`.
    try {
      return JSON.stringify(value);
    } catch {
      // Reference circulaire : le cas ne devrait pas venir d'une reponse JSON,
      // mais l'exception ne doit pas casser le rendu d'une ligne de tableau.
      return '';
    }
  }

  // `bigint`, `symbol`, `function` : aucun n'apparait dans une reponse JSON.
  // La conversion reste explicite plutot que de tomber dans un cas par defaut muet.
  return typeof value === 'bigint' ? value.toString() : '';
};

/**
 * Forme **normalisee pour la comparaison**, distincte de la forme d'affichage.
 *
 * La distinction n'est pas cosmetique, elle corrige deux defauts reels :
 *
 * - `asDisplayString(true)` rend « Oui ». Compare a la chaine `'true'` que la
 *   barre de filtres de React Admin transmet pour une case cochee, l'egalite
 *   echouerait : le filtre ne trouverait jamais rien.
 * - `asDisplayString(1000)` rend « 1 000 », separateur de milliers compris.
 *   Compare a `'1000'` saisi dans un filtre, l'egalite echouerait aussi — et le
 *   defaut serait invisible sur les petits nombres, donc decouvert tard.
 *
 * Cette forme est donc brute et stable : jamais localisee, jamais habillee.
 */
const asComparableString = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }

  if (typeof value === 'string') {
    return value;
  }

  return asDisplayString(value);
};

/**
 * Comparaison d'egalite entre une valeur d'enregistrement et une valeur de filtre.
 *
 * Passe par `asComparableString` pour ne jamais comparer deux `[object Object]`,
 * ce qui rendrait tous les objets egaux entre eux, ni deux formes localisees d'un
 * meme nombre.
 */
export const looselyEquals = (left: unknown, right: unknown): boolean =>
  asComparableString(left) === asComparableString(right);

/**
 * Identifiant d'enregistrement en chaine.
 *
 * React Admin autorise un identifiant `string | number` : les deux formes doivent
 * se comparer sans surprise, et un identifiant absent ne doit pas devenir la
 * chaine `"undefined"`.
 */
export const asIdString = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';
